// JS
import React, { useState, useEffect } from 'react';

import { randomId, useTailwindMediaQuery } from '../shared/utils';

// COMPONENT
const BrandList = ({ allBrandsData, gaBlockName }) => {
  const uniqueId = randomId();
  const isUnderXl = useTailwindMediaQuery('xl');
  const alphabet = '_#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const windowUrl = window.location.href;

  const [brandCount, setBrandCount] = useState(0);
  const [brands, setBrands] = useState([]);
  const [active, setActive] = useState('All');
  const [columnisedBrands, setColumnisedBrands] = useState([]);

  // Set active brand if url has get query of brandletter
  useEffect(() => {
    const url = new URL(windowUrl);
    const brandLetter = url.searchParams.get('brandletter')?.toUpperCase();
    if (brandLetter && alphabet.includes(brandLetter)) {
      setActive(brandLetter);
    }
  }, []);

  useEffect(() => {
    if (allBrandsData && allBrandsData.hierarchy) {
      var count = 0;
      var brandsArr = {};
      allBrandsData.hierarchy.forEach((brand) => {
        var letter = brand.name.substring(0, 1).toUpperCase();
        if (!isNaN(letter)) letter = '#';
        if (letter.includes('_')) letter = '_';
        if (!(letter in brandsArr)) brandsArr[letter] = [];
        brandsArr[letter].push(brand);
        count++;
      });
      setBrands(brandsArr);
      setBrandCount(count);
    }
  }, [allBrandsData]);

  useEffect(() => {
    var result = [];

    if (isUnderXl) {
      result = [
        ['_', '#', 'A', 'B', 'C', 'D', 'E', 'F'],
        ['G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O'],
        ['P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
      ];
    } else {
      result = [
        ['_', '#', 'A', 'B', 'C'],
        ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'],
        ['L', 'M', 'N', 'O', 'P', 'Q'],
        ['R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z']
      ];
    }

    setColumnisedBrands(result);
  }, [isUnderXl]);

  const handleLetterClick = (letter) => {
    if (letter in brands || letter === 'All') {
      setActive(letter);
    }
  };

  if (brands?.length === 0) return null;

  return (
    <section id={'brand-list-' + uniqueId} className="container my-16 md:my-32">
      <div
        data-name={'Brands'}
        data-creative={gaBlockName}
        data-id={'brands-' + uniqueId}
        data-position={'banner-slot-1'}
        data-type="banner"
      >
        <h2 className="text-xl text-center font-semibold uppercase tracking-widest md:text-left mb-8">
          Brands <span className="font-base">({brandCount})</span>
        </h2>
        <div className="scroll-none flex items-center justify-between overflow-x-auto -mx-6 px-6 mb-12 md:mb-20 xl:mb-32">
          <a
            className={
              'block md:hidden text-md md:text-lg uppercase pr-4 cursor-pointer font-semibold mr-4 ' +
              (active === 'All'
                ? 'cursor-default text-black font-semibold'
                : 'cursor-pointer text-gray-400 hover:text-gray-600')
            }
            onClick={() => handleLetterClick('All')}
            data-name={'All'}
            data-creative={gaBlockName}
            data-id={'brand-all'}
            data-position={'button-slot-all'}
            data-type="banner-button"
          >
            All
          </a>
          {alphabet.map((letter, index) => (
            <div
              key={index}
              className={
                'text-lg md:text-xl px-4 ' +
                (index === 0 ? 'md:pl-0 ' : '') +
                (active === letter
                  ? 'cursor-default text-black font-semibold '
                  : letter in brands
                  ? 'cursor-pointer text-gray-400 hover:text-gray-600'
                  : 'cursor-default text-gray-300')
              }
              onClick={() => handleLetterClick(letter)}
              data-name={letter}
              data-creative={gaBlockName}
              data-id={'brand-' + letter}
              data-position={'button-slot-' + (index + 1)}
              data-type="banner-button"
            >
              {letter}
            </div>
          ))}
          <a
            className={
              'hidden md:block text-md md:text-lg uppercase pl-4 cursor-pointer font-semibold ml-4 lg:ml-14 ' +
              (active === 'All'
                ? 'cursor-default text-black font-semibold'
                : 'cursor-pointer text-gray-400 hover:text-gray-600')
            }
            onClick={() => handleLetterClick('All')}
            data-name={'All'}
            data-creative={gaBlockName}
            data-id={'brand-all'}
            data-position={'button-slot-all'}
            data-type="banner-button"
          >
            All
          </a>
        </div>
        <div className="flex flex-wrap flex-col md:flex-row -mx-4">
          {columnisedBrands.map((column, columnIndex) => {
            return (
              <div
                key={columnIndex}
                className={
                  'w-full md:w-1/3 xl:w-1/4 px-4' +
                  (!column.includes(active) && active !== 'All'
                    ? ' hidden'
                    : '')
                }
              >
                {column.map((letter, index) => {
                  var brandList = brands[letter];

                  if (brandList === undefined) return null;
                  brandList.sort((a, b) => {
                    if (a.name.toLowerCase() < b.name.toLowerCase()) {
                      return -1;
                    }
                    if (a.name.toLowerCase() > b.name.toLowerCase()) {
                      return 1;
                    }
                    return 0;
                  });

                  return (
                    <div
                      key={'brands-list-group-' + letter}
                      data-brands-group={letter}
                      className={
                        'w-full mb-10 md:mb-18' +
                        (active === 'All'
                          ? ''
                          : active === letter
                          ? ''
                          : ' hidden')
                      }
                    >
                      <div className="brands-list-group-item">
                        <div className="font-semibold mb-4">{letter}</div>
                        {brandList === undefined ? (
                          <div className="text-gray-500 text-sm mb-1">
                            No brands found
                          </div>
                        ) : (
                          brandList.map((brand, j) => (
                            <a
                              key={j}
                              href={brand.data.url}
                              className="text-sm leading-8 mb-1 hover:underline"
                              data-name={brand.name}
                              data-creative={gaBlockName}
                              data-id={brand.code}
                              data-position={
                                'button-slot-' +
                                (columnIndex + 1) +
                                '-' +
                                (index + 1) +
                                '-' +
                                (j + 1)
                              }
                              data-type="banner-button"
                            >
                              {brand.name}
                            </a>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BrandList;
